import qs from "qs";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { postLogout } from "services/api/taptype/auth";
import { actions as authActions, isAuthenticatedSelector, userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import { apiHandler, getPharmacySegmentFromSegments, getRouteLocale, isOpenedInIframe } from "utils/helpers";
import vars from "utils/vars";

const useLogoutIdleUser = () => {
	const { i18n } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// redux global state
	const user = useSelector(userSelector);
	const isAuthenticated = useSelector(isAuthenticatedSelector);
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);

	// constants
	const isDemoTenant = Boolean(user?.tenant?.is_demo);
	const inIframe = isOpenedInIframe();

	// ref hook
	const logoutCancelRequestRef = useRef(null);

	// memo hook
	const pharmacySegment = useMemo(() => getPharmacySegmentFromSegments(segments), [segments]);

	// event handlers
	const onUserBeingIdleHandler = async () => {
		if (logoutCancelRequestRef.current?.signal) logoutCancelRequestRef.current?.abort();
		logoutCancelRequestRef.current = new AbortController();

		await apiHandler(postLogout, {
			data: { body: { email: user?.email }, options: { signal: logoutCancelRequestRef.current.signal } },
			onSuccess: () => {
				setTimeout(() => {
					navigate(
						`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
							...(pharmacySegment?.id === globals?.segment?.id
								? { segment_id: pharmacySegment?.id }
								: {}),
						})}`
					);
				}, 0);
				dispatch(authActions.logout());
			},
		});
	};

	// custom idle timer hook
	useIdleTimer({
		...(+vars.app.idleTimeOutInMinutes ? { timeout: 1000 * 60 * vars.app.idleTimeOutInMinutes } : {}),
		startOnMount: !inIframe,
		crossTab: !inIframe,
		syncTimers: inIframe ? 0 : 200,
		onIdle: () => {
			if (inIframe) return;
			if (isAuthenticated && !isDemoTenant) onUserBeingIdleHandler();
		},
	});

	useEffect(() => {
		return () => {
			if (logoutCancelRequestRef.current?.signal) logoutCancelRequestRef.current?.abort();
		};
	}, []);
};

export default useLogoutIdleUser;
